import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { FiCheckCircle, FiTool, FiShield, FiClock, FiDisc } from 'react-icons/fi';
import { FaWhatsapp } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import serviceImg from '../../assets/service.jpg';
import styles from './Services.module.css';

const WHATSAPP = 'https://wa.me/16726676878?text=Hi%20Murat%2C%20I%20would%20like%20to%20book%20an%20oil%20change%20service.';

export default function Services() {
  const { t } = useTranslation();
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });

  const included = [
    t('services.included.oil'),
    t('services.included.brake'),
    t('services.included.obd'),
    t('services.included.brakeInspection'),
    t('services.included.fluid'),
    t('services.included.freeCheckup'),
    t('services.included.noDriveway'),
  ];

  const reasons = [
    { icon: <FiTool size={22}/>, title: t('services.reasons.pro.title'), text: t('services.reasons.pro.text') },
    { icon: <FiDisc size={22}/>, title: t('services.reasons.brake.title'), text: t('services.reasons.brake.text') },
    { icon: <FiClock size={22}/>, title: t('services.reasons.flex.title'), text: t('services.reasons.flex.text') },
    { icon: <FiShield size={22}/>, title: t('services.reasons.quality.title'), text: t('services.reasons.quality.text') },
  ];

  return (
    <section id="services" className={styles.section} ref={ref}>
      <div className={styles.container}>
        <motion.div className={styles.header}
          initial={{ opacity: 0, y: 24 }} animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}>
          <span className={styles.label}><span className={styles.labelLine}/> {t('services.label')}</span>
          <h2 className={styles.heading}>{t('services.heading').split('—')[0]}— <span className={styles.accent}>{t('services.heading').split('—')[1]}</span></h2>
          <p className={styles.sub}>{t('services.sub')}</p>
        </motion.div>

        <div className={styles.main}>
          <motion.div className={styles.imgWrap}
            initial={{ opacity: 0, x: -30 }} animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.7, delay: 0.2 }}>
            <img src={serviceImg} alt="Oil change service" className={styles.img} />
            <div className={styles.imgBadge}>
              <FaWhatsapp size={20}/>
              <span>{t('services.bookIn60')}</span>
            </div>
          </motion.div>

          <motion.div className={styles.details}
            initial={{ opacity: 0, x: 30 }} animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.7, delay: 0.3 }}>
            <h3 className={styles.detailTitle}>{t('services.detailTitle')}</h3>
            <p className={styles.detailText}>{t('services.detailText')}</p>
            <ul className={styles.checklist}>
              {included.map(item => (
                <li key={item} className={styles.checkItem}>
                  <FiCheckCircle className={styles.checkIcon} size={16}/>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            <div className={styles.actions}>
              <a href={WHATSAPP} target="_blank" rel="noreferrer" className={styles.btnWa}>
                <FaWhatsapp size={17}/> {t('services.bookWhatsapp')}
              </a>
              <a href="https://www.fram.com/" target="_blank" rel="noreferrer" className={styles.btnParts}>
                {t('services.lookParts')}
              </a>
            </div>
          </motion.div>
        </div>

        <div className={styles.cards}>
          {reasons.map((r, i) => (
            <motion.div key={r.title} className={styles.card}
              initial={{ opacity: 0, y: 30 }} animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.4 + i * 0.12 }}>
              <div className={styles.cardIcon}>{r.icon}</div>
              <h4 className={styles.cardTitle}>{r.title}</h4>
              <p className={styles.cardText}>{r.text}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
